/**
 * Challenge Lottie paths under src/assets.
 * Firebase Storage JSON URLs often fail in-browser with CORS — prefer local files.
 */
export const CHALLENGE_LOTTIE_ASSET_BASE =
  '/assets/lotti_animation/game-new/challanges-lottie-files/';

/** Encode filename so `+` (e.g. contianer+padlock.json) is not treated as a space. */
function toLocalAssetPath(fileName: string): string {
  const file = fileName.replace(/^\/+/, '').split(/[/\\]/).pop() || fileName;
  return `${CHALLENGE_LOTTIE_ASSET_BASE}${encodeURIComponent(file).replace(/%2F/g, '/')}`;
}

/** Map remote Firebase / relative file refs to a same-origin local asset path when possible. */
export function resolveChallengeLottiePath(
  urlOrFile: string | null | undefined,
  localFallbackFile?: string
): string {
  const fallback = localFallbackFile
    ? localFallbackFile.startsWith('/assets/')
      ? localFallbackFile
      : toLocalAssetPath(localFallbackFile)
    : '';

  if (!urlOrFile) {
    return fallback;
  }

  const value = urlOrFile.trim();
  if (!value) {
    return fallback;
  }

  // Already a same-origin asset — keep it (still encode + in the leaf name).
  if (value.startsWith('/assets/') || value.startsWith('assets/')) {
    const normalized = value.startsWith('/') ? value : `/${value}`;
    const leaf = normalized.split('/').pop() || '';
    if (leaf.includes('+')) {
      return toLocalAssetPath(leaf);
    }
    return normalized;
  }

  // Relative filename only.
  if (!/^https?:\/\//i.test(value) && value.toLowerCase().endsWith('.json')) {
    return toLocalAssetPath(value);
  }

  // Firebase / any remote URL → extract *.json and map to local (avoids CORS).
  if (/^https?:\/\//i.test(value)) {
    try {
      const decoded = decodeURIComponent(value);
      const match = decoded.match(/([^/?#]+\.json)/i);
      if (match?.[1]) {
        return toLocalAssetPath(match[1]);
      }
    } catch {
      // ignore decode errors
    }
    return fallback || value;
  }

  return fallback || value;
}
